import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, MenuItem, Stack, TextField, Typography} from '@mui/material';
import {FORM_ACCESS_LEVELS} from '../../../../../../constants/formAccessLevels';
import {
    useGetFormsQuery,
    useLazyGetFormQuery
} from '../../../../../../api/services/formsService';

const SOURCE_ACCESS_LEVELS = new Set([
    FORM_ACCESS_LEVELS.private,
    FORM_ACCESS_LEVELS.public,
    FORM_ACCESS_LEVELS.publicWithStudentLink
]);

export const PREFILLABLE_COMPONENT_TYPES = new Set([
    'singleText',
    'textBox',
    'numericInputBox',
    'checkboxQuestion',
    'employeeSelect',
    'dateTime',
    'date',
    'offerStatusChoice',
    'address',
    'upn',
    'previousSchoolName',
    'previousSchoolEmail'
]);

const getComponentLabel = (component) => {
    const description = (component?.description || component?.formattedDescription || '')
        .replace(/<[^>]*>/g, '')
        .trim();
    return description || component?.label || component?.type || 'Untitled component';
};

const PrefillFromSettings = ({
                                 index,
                                 field,
                                 formId,
                                 accessLevel,
                                 isFormArchived,
                                 isEditMode = true,
                                 onChange
                             }) => {
    const isSupportedForm = accessLevel !== FORM_ACCESS_LEVELS.public;
    const isPrefillable = PREFILLABLE_COMPONENT_TYPES.has(field?.type);

    const selectedFormId = field?.prefillFrom?.formId ?? '';
    const selectedComponentId = field?.prefillFrom?.componentId ?? '';
    const hasPrefillConfigured = Boolean(selectedFormId);

    const [isConfiguring, setIsConfiguring] = useState(hasPrefillConfigured);

    useEffect(() => {
        if (hasPrefillConfigured) {
            setIsConfiguring(true);
        }
    }, [hasPrefillConfigured]);

    const showConfiguration = isEditMode && (isConfiguring || hasPrefillConfigured);
    const showSummary = !isEditMode && hasPrefillConfigured;

    const {
        data: formsResponse,
        isLoading: isFormsLoading
    } = useGetFormsQuery({
        pageSize: 1000,
        pageNumber: 0,
        filters: []
    }, {
        skip: !isSupportedForm || !isPrefillable || !showConfiguration
    });

    const [getForm] = useLazyGetFormQuery();
    const [sourceForm, setSourceForm] = useState(null);
    const [isSchemaLoading, setIsSchemaLoading] = useState(false);

    const availableForms = useMemo(() => {
        const items = formsResponse?.items ?? [];

        return items
            .filter((form) => {
                const formAccessLevel = Number(form.accessLevel ?? form.AccessLevel);
                if (!SOURCE_ACCESS_LEVELS.has(formAccessLevel)) {
                    return false;
                }

                if (formId && form.id === formId) {
                    return false;
                }

                if (form.isCommunication) {
                    return false;
                }

                const status = String(form.status ?? '').toLowerCase();
                if (status === 'deleted' || status === 'archived' || status === 'communication') {
                    return false;
                }

                return true;
            })
            .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
    }, [formId, formsResponse?.items]);

    useEffect(() => {
        let cancelled = false;

        const loadSourceForm = async () => {
            if (!selectedFormId || (!showConfiguration && !showSummary)) {
                setSourceForm(null);
                return;
            }

            setIsSchemaLoading(true);
            try {
                const form = await getForm(selectedFormId).unwrap();
                if (!cancelled) {
                    setSourceForm(form ?? null);
                }
            } catch {
                if (!cancelled) {
                    setSourceForm(null);
                }
            } finally {
                if (!cancelled) {
                    setIsSchemaLoading(false);
                }
            }
        };

        loadSourceForm();

        return () => {
            cancelled = true;
        };
    }, [getForm, selectedFormId, showConfiguration, showSummary]);

    const sourceSchema = Array.isArray(sourceForm?.schema) ? sourceForm.schema : null;

    const matchingComponents = useMemo(() => {
        if (!Array.isArray(sourceSchema) || !field?.type) {
            return [];
        }

        return sourceSchema.filter((component) => component?.type === field.type);
    }, [field?.type, sourceSchema]);

    const selectedSourceComponent = useMemo(() => {
        if (!selectedComponentId || !Array.isArray(sourceSchema)) {
            return null;
        }

        return sourceSchema.find((component) => component.id === selectedComponentId) ?? null;
    }, [selectedComponentId, sourceSchema]);

    const summaryText = useMemo(() => {
        if (!hasPrefillConfigured) {
            return '';
        }

        const formName = sourceForm?.name || (isSchemaLoading ? '…' : `Form #${selectedFormId}`);
        const componentLabel = selectedSourceComponent
            ? getComponentLabel(selectedSourceComponent)
            : (selectedComponentId
                ? (isSchemaLoading ? '…' : 'Selected field')
                : 'Field not selected');

        return `Prefill: ${formName} → ${componentLabel}`;
    }, [
        hasPrefillConfigured,
        sourceForm?.name,
        isSchemaLoading,
        selectedFormId,
        selectedSourceComponent,
        selectedComponentId
    ]);

    if (!isSupportedForm || !isPrefillable) {
        return null;
    }

    if (!isEditMode) {
        if (!showSummary) {
            return null;
        }

        return (
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: 'block',
                    mt: 1,
                    fontStyle: 'italic'
                }}
            >
                {summaryText}
            </Typography>
        );
    }

    const handleEnable = () => {
        setIsConfiguring(true);
    };

    const handleClear = () => {
        setIsConfiguring(false);
        onChange({index, prefillFrom: null});
    };

    const handleFormChange = (event) => {
        const nextFormId = event.target.value;
        if (!nextFormId) {
            onChange({index, prefillFrom: null});
            return;
        }

        onChange({
            index,
            prefillFrom: {
                formId: Number(nextFormId),
                componentId: null
            }
        });
    };

    const handleComponentChange = (event) => {
        const nextComponentId = event.target.value;
        if (!selectedFormId || !nextComponentId) {
            onChange({
                index,
                prefillFrom: selectedFormId
                    ? {
                        formId: Number(selectedFormId),
                        componentId: null
                    }
                    : null
            });
            return;
        }

        onChange({
            index,
            prefillFrom: {
                formId: Number(selectedFormId),
                componentId: nextComponentId
            }
        });
    };

    if (!showConfiguration) {
        return (
            <Box sx={{mt: 1}}>
                <Button
                    variant="text"
                    size="small"
                    disabled={isFormArchived}
                    onClick={handleEnable}
                    sx={{
                        px: 0,
                        textTransform: 'none',
                        justifyContent: 'flex-start'
                    }}
                >
                    Prefill from previous form
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{mt: 2, pt: 2, borderTop: '1px solid #e5e7eb'}}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{mb: 1}}
            >
                <Typography variant="subtitle2">
                    Prefill from previous form response
                </Typography>
                <Button
                    variant="text"
                    size="small"
                    disabled={isFormArchived}
                    onClick={handleClear}
                    sx={{textTransform: 'none', flexShrink: 0}}
                >
                    Remove
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                When a recipient opens this form, this field can be filled from the latest submitted
                response on another form for the same student or employee.
            </Typography>
            <Stack spacing={2}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Source form"
                    value={selectedFormId}
                    disabled={isFormArchived || isFormsLoading}
                    onChange={handleFormChange}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {availableForms.map((form) => (
                        <MenuItem key={form.id} value={form.id}>
                            {form.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Source component"
                    value={selectedComponentId || ''}
                    disabled={isFormArchived || !selectedFormId || isSchemaLoading}
                    onChange={handleComponentChange}
                    helperText={
                        selectedFormId && !isSchemaLoading && matchingComponents.length === 0
                            ? `No ${field.type} components found on the selected form`
                            : 'Only components of the same type can be selected'
                    }
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {matchingComponents.map((component) => (
                        <MenuItem key={component.id} value={component.id}>
                            {getComponentLabel(component)}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
        </Box>
    );
};

PrefillFromSettings.propTypes = {
    index: PropTypes.number.isRequired,
    field: PropTypes.object.isRequired,
    formId: PropTypes.number,
    accessLevel: PropTypes.number,
    isFormArchived: PropTypes.bool,
    isEditMode: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

export default PrefillFromSettings;
